import React, { useEffect, useState, useRef } from "react";
import { StudentDashBoard } from "./StudentDashBoard";
import { useDispatch, useSelector } from "react-redux";
import { getIsActive, getUserInfo } from "../../store/auth/selectors";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { useEventsMutation } from "../../store/ApiMutaion";
import { StudentPastDashBoard } from "./scheduledEvents";
import { motion, AnimatePresence } from "framer-motion";
import { getExamStatus } from "../../store/app/selectors";
import { userLogout } from "../../store/auth/auth.slice";
import { ReloginPopUp } from "../components/modal/ReloginPopup";
import { setModalState } from "../../store/app/app.slice";
import { clearPostSignupOverlay, getPostSignupOverlay } from "../../utils/postSignupOverlay.utils";
import { CONFIG_VARS } from "../../constants/configuration";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { store } from "../../store";

const DEEPLINK_OVERLAY_FALLBACK_MS = 60000;
const FETCH_RETRY_DELAY_MS = 2000;
const MAX_FETCH_ATTEMPTS = 3;
const DEBOUNCE_DELAY_MS = 500;

const isDeeplinkOverlayWaitForEventsEnabled = () =>
  CONFIG_VARS?.featureFlags?.deeplinkSignupOverlayWaitForEvents ?? false;

export const DashboardPage = () => {
  const isActive = useSelector(getIsActive);
  const user = useSelector(getUserInfo);
  const history = useHistory();
  const exam_status = useSelector(getExamStatus);
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const createdEventMappings = useSelector((state) => state.auth.createdEventMappings);

  const [events, setEvents] = useState([]);
  const [thisWeekEvents, setThisWeekEvents] = useState([]);
  const [pastUnAttemptedEvents, setPastUnAttemptedEvents] = useState([]);
  const [pastAttemptedEvents, setPastAttemptedEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("scheduled");
  const [fetchEvents] = useEventsMutation();
  const refetchTimeoutRef = useRef(null);
  const fetchAttemptRef = useRef(0);
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const debounceTimeoutRef = useRef(null);
  const initialFetchDoneRef = useRef(false); // Track if initial fetch is done
  const waitForEventsBeforeClearOverlay = isDeeplinkOverlayWaitForEventsEnabled();
  const [hasTrackedDashboardView, setHasTrackedDashboardView] = useState(false);
  const [hasTrackedScheduledTab, setHasTrackedScheduledTab] = useState(false);
  const [hasTrackedEmptyState, setHasTrackedEmptyState] = useState(false);

  const isEventInFetchResponse = (fetchData, crnId, eventId) => {
    const inScheduled = fetchData?.scheduled_events?.some((e) => e.crn_id === crnId && e.event_id === eventId);
    if (inScheduled) return true;

    const inPastUnattempted = fetchData?.past_events?.unattempted_events?.some(
      (e) => e.crn_id === crnId && e.event_id === eventId
    );
    if (inPastUnattempted) return true;

    const inPastAttempted = fetchData?.past_events?.attempted_events?.some(
      (e) => e.crn_id === crnId && e.event_id === eventId
    );

    return inPastAttempted;
  };

  const checkHasLiveTest = (eventsList) => {
    return eventsList.some(
      (event) =>
        event.is_exam_status === "STARTED" || event.is_exam_status === "INITIATED" || event.can_resume_exam === true
    );
  };

  const hasFetchedEventsFromApi = (responseData) =>
    (responseData?.scheduled_events?.length || 0) > 0 ||
    (responseData?.past_events?.unattempted_events?.length || 0) > 0 ||
    (responseData?.past_events?.attempted_events?.length || 0) > 0;

  const mapMappingToScheduledEvent = (mapping, latestUser) => ({
    title: mapping?.event_details?.name,
    url: mapping?.event_details?.provider_metadata?.event_link,
    startRecur: mapping?.event_details?.start_date_time,
    endRecur: mapping?.event_details?.end_date_time,
    exam_attempted: mapping?.exam_attempted || false,
    crn_id: mapping?.crn_id,
    event_id: mapping?.event_id,
    event_code: mapping?.event_details?.event_code,
    grade_id: mapping?.student_details?.grade_id,
    level: mapping?.event_details?.level,
    examType: mapping?.event_details?.exam_name,
    StudentExamType: latestUser?.exam,
    qu_id: mapping?.event_details?.question_paper_id,
    round: mapping?.event_details?.round,
    examStartedAt: mapping?.exam_started_at,
    examDurationInMinutes: mapping?.event_details?.exam_duration_in_minutes?.toString(),
    submitedAt: mapping?.exam_attempted_at,
    is_paid_report: mapping?.event_details?.is_paid_report,
    event_type: mapping?.event_details?.event_type,
    event_date: mapping?.event_details?.start_date_time,
    can_resume_exam: mapping?.can_resume_exam || false,
    exam_started_flag: mapping?.is_exam_started || false,
    is_exam_status: mapping?.exam_status,
    source: "scheduled",
    is_demo_booked: mapping?.is_demo_booked,
    certificate_link: mapping?.student_report?.certificate
  });

  const buildMappedScheduledEvents = (latestMappings, latestUser, responseData = null) => {
    const userCrnId = latestUser?.crn_id;
    if (!latestMappings?.length || !userCrnId) return [];

    const userMappings = latestMappings.filter((m) => String(m.crn_id) === String(userCrnId));

    return userMappings
      .filter((mapping) => {
        if (mapping?.event_details?.event_type?.toLowerCase() === "offline") return false;
        if (responseData && isEventInFetchResponse(responseData, mapping.crn_id, mapping.event_id)) return false;
        return true;
      })
      .map((mapping) => mapMappingToScheduledEvent(mapping, latestUser));
  };

  const applyCreatedMappingsToDashboard = () => {
    const { createdEventMappings: latestMappings, user: latestUser } = store.getState().auth;
    const mappedEvents = buildMappedScheduledEvents(latestMappings, latestUser);

    if (mappedEvents.length > 0) {
      setThisWeekEvents((prevEvents) => {
        const existingEventKeys = new Set(prevEvents.map((e) => `${e.crn_id}_${e.event_id}`));
        const newEvents = mappedEvents.filter((e) => !existingEventKeys.has(`${e.crn_id}_${e.event_id}`));
        return [...prevEvents, ...newEvents];
      });
      setEvents((prevEvents) => {
        const existingEventKeys = new Set(prevEvents.map((e) => `${e.crn_id}_${e.event_id}`));
        const newEvents = mappedEvents.filter((e) => !existingEventKeys.has(`${e.crn_id}_${e.event_id}`));
        return [...prevEvents, ...newEvents];
      });
    }
  };

  const scheduleFetchRetryIfNeeded = (hasApiEvents) => {
    if (hasApiEvents) {
      fetchAttemptRef.current = MAX_FETCH_ATTEMPTS;
      return;
    }

    if (fetchAttemptRef.current >= MAX_FETCH_ATTEMPTS) return;

    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }

    refetchTimeoutRef.current = setTimeout(() => {
      fetchAttemptRef.current += 1;
      fetchEvent({ background: true });
    }, FETCH_RETRY_DELAY_MS);
  };

  const fetchEvent = async ({ background = false, forceRetry = false } = {}) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      console.log("Fetch already in progress, skipping...");
      return;
    }

    const now = Date.now();
    // Prevent rapid successive calls (within 500ms)
    if (now - lastFetchTimeRef.current < DEBOUNCE_DELAY_MS && !forceRetry) {
      console.log("Fetch called too soon, skipping...");
      return;
    }

    if (forceRetry) {
      fetchAttemptRef.current = 1;
    }

    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;
    const startTime = now;

    if (!background) {
      setIsLoading(true);
    }

    try {
      const res = await fetchEvents();
      const responseTimeMs = Date.now() - startTime;

      if (!res?.data?.error) {
        let responseData = res?.data;
        const { createdEventMappings: latestMappings, user: latestUser } = store.getState().auth;
        let scheduledEvents =
          responseData?.scheduled_events
            ?.filter((event) => event.event_details?.event_type !== "OFFLINE")
            ?.map((value) => ({
              title: value?.event_details?.name,
              url: value?.event_details?.provider_metadata?.event_link,
              startRecur: value?.event_details?.start_date_time,
              endRecur: value?.event_details?.end_date_time,
              exam_attempted: value?.exam_attempted,
              crn_id: value?.crn_id,
              event_id: value?.event_id,
              event_code: value?.event_details?.event_code,
              grade_id: value?.student_details?.grade_id,
              level: value?.event_details?.level,
              examType: value?.event_details?.exam_name,
              StudentExamType: latestUser?.exam,
              qu_id: value?.event_details?.question_paper_id,
              round: value?.event_details?.round,
              examStartedAt: value?.exam_started_at,
              examDurationInMinutes: value?.event_details?.exam_duration_in_minutes?.toString(),
              submitedAt: value?.exam_attempted_at,
              is_paid_report: value?.event_details?.is_paid_report,
              event_type: value?.event_details?.event_type,
              event_date: value?.event_details?.start_date_time,
              can_resume_exam: value?.can_resume_exam,
              exam_started_flag: value?.is_exam_started,
              is_exam_status: value?.exam_status,
              source: "scheduled",
              is_demo_booked: value?.is_demo_booked,
              certificate_link: value?.student_report?.certificate
            })) || [];

        const mappedCreatedEvents = buildMappedScheduledEvents(latestMappings, latestUser, responseData);
        if (mappedCreatedEvents.length > 0) {
          scheduledEvents = [...scheduledEvents, ...mappedCreatedEvents];
        }

        const hasApiEvents = hasFetchedEventsFromApi(responseData);

        // Only schedule retry if this is not a background fetch and we have no events
        if (!background && !hasApiEvents && fetchAttemptRef.current < MAX_FETCH_ATTEMPTS) {
          scheduleFetchRetryIfNeeded(hasApiEvents);
        } else if (hasApiEvents) {
          fetchAttemptRef.current = MAX_FETCH_ATTEMPTS;
        }

        const pastUnAttempted =
          responseData?.past_events?.unattempted_events?.map((value) => ({
            title: value?.event_details?.name,
            url: value?.event_details?.provider_metadata?.event_link,
            startRecur: value?.event_details?.start_date_time,
            endRecur: value?.event_details?.end_date_time,
            exam_attempted: value?.exam_attempted,
            crn_id: value?.crn_id,
            event_id: value?.event_id,
            event_code: value?.event_details?.event_code,
            grade_id: value?.student_details?.grade_id,
            level: value?.event_details?.level,
            examType: value?.event_details?.exam_name,
            StudentExamType: latestUser?.exam,
            event_provider: value?.event_details?.provider_metadata?.event_provider,
            qu_id: value?.event_details?.question_paper_id,
            round: value?.event_details?.round,
            examStartedAt: value?.exam_started_at,
            examDurationInMinutes: value?.event_details?.exam_duration_in_minutes?.toString(),
            submitedAt: value?.exam_attempted_at,
            is_paid_report: value?.event_details?.is_paid_report,
            event_type: value?.event_details?.event_type,
            event_date: value?.event_details?.start_date_time,
            can_resume_exam: value?.can_resume_exam,
            exam_started_flag: value?.is_exam_started,
            is_exam_status: value?.exam_status,
            source: "past_unattempted",
            is_demo_booked: value?.is_demo_booked,
            certificate_link: value?.student_report?.certificate
          })) || [];

        const pastAttempted =
          responseData?.past_events?.attempted_events?.map((value) => ({
            title: value?.event_details?.name,
            url: value?.event_details?.provider_metadata?.event_link,
            startRecur: value?.event_details?.start_date_time,
            endRecur: value?.event_details?.end_date_time,
            exam_id: value?.event_details?.exam_id,
            exam_attempted: value?.exam_attempted,
            crn_id: value?.crn_id,
            event_id: value?.event_id,
            event_code: value?.event_details?.event_code,
            grade_id: value?.student_details?.grade_id,
            event_provider: value?.event_details?.provider_metadata?.event_provider,
            level: value?.event_details?.level,
            examType: value?.event_details?.exam_name,
            StudentExamType: latestUser?.exam,
            qu_id: value?.event_details?.question_paper_id,
            round: value?.event_details?.round,
            examStartedAt: value?.exam_started_at,
            examDurationInMinutes: value?.event_details?.exam_duration_in_minutes?.toString(),
            submitedAt: value?.exam_attempted_at,
            is_paid_report: value?.event_details?.is_paid_report,
            event_type: value?.event_details?.event_type,
            event_date: value?.event_details?.start_date_time,
            can_resume_exam: value?.can_resume_exam,
            exam_started_flag: value?.is_exam_started,
            is_exam_status: value?.exam_status,
            source: "past_attempted",
            is_demo_booked: value?.is_demo_booked,
            certificate_link: value?.student_report?.certificate
          })) || [];

        setThisWeekEvents(scheduledEvents);
        setEvents(scheduledEvents);
        setPastUnAttemptedEvents(pastUnAttempted);
        setPastAttemptedEvents(pastAttempted);

        if (waitForEventsBeforeClearOverlay) {
          const overlay = getPostSignupOverlay();
          const overlayElapsed = overlay?.updatedAt ? Date.now() - overlay.updatedAt : 0;
          const hasFetchedEvents = hasApiEvents || mappedCreatedEvents.length > 0;

          if (hasFetchedEvents && overlay) {
            clearPostSignupOverlay();
          } else if (!hasFetchedEvents && overlay && overlayElapsed >= DEEPLINK_OVERLAY_FALLBACK_MS) {
            clearPostSignupOverlay();
          }
        }

        const hasValidScheduledEvents = scheduledEvents.some((event) => {
          const normalizedStudentExam = event.StudentExamType?.toLowerCase().replace(/\s+/g, "");
          const normalizedEventExam = event.examType?.toLowerCase().replace(/\s+/g, "");

          const isJeeNeet = normalizedEventExam.includes("jee") || normalizedEventExam.includes("neet");
          if (isJeeNeet) {
            return (
              normalizedStudentExam?.includes(normalizedEventExam) ||
              normalizedEventExam?.includes(normalizedStudentExam)
            );
          }
          return true;
        });

        const hasPastEvents = pastUnAttempted.length + pastAttempted.length > 0;

        if (!hasValidScheduledEvents && hasPastEvents) {
          setSelectedTab("past");
        }

        // Mark initial fetch as done
        initialFetchDoneRef.current = true;
      } else if (res?.data?.error || res?.statusCode !== 200) {
        console.error("Error:", res?.data?.error);
        const httpStatus = res?.statusCode || 500;
        analytics.trackDashboardEventsApiFailed(httpStatus, responseTimeMs);
        if (res?.data?.error?.message === "UnAuthorized") {
          fetchAttemptRef.current = MAX_FETCH_ATTEMPTS;
          history.push(ROUTES.STUDENT);
          dispatch(userLogout());
          dispatch(setModalState({ content: <ReloginPopUp />, open: true }));
        } else if (!background && fetchAttemptRef.current < MAX_FETCH_ATTEMPTS) {
          scheduleFetchRetryIfNeeded(false);
        } else {
          // Even on error, mark initial fetch as done and stop loading
          initialFetchDoneRef.current = true;
        }
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      analytics.trackDashboardEventsApiFailed(500, Date.now() - startTime);
      if (!background && fetchAttemptRef.current < MAX_FETCH_ATTEMPTS) {
        scheduleFetchRetryIfNeeded(false);
      } else {
        // Even on error, mark initial fetch as done and stop loading
        initialFetchDoneRef.current = true;
      }
    } finally {
      isFetchingRef.current = false;
      if (!background) {
        setIsLoading(false);
      } else if (initialFetchDoneRef.current) {
        // Ensure loading is false after background fetch completes initial load
        setIsLoading(false);
      }
    }
  };

  // Debounced version of fetch for createdEventMappings changes
  const debouncedFetchEvent = useRef((...args) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      fetchEvent(...args);
    }, DEBOUNCE_DELAY_MS);
  }).current;

  // Initial fetch
  useEffect(() => {
    if (!isActive) {
      history.replace(ROUTES.SCHOOL_ONBOARDING);
      return undefined;
    }

    fetchAttemptRef.current = 1;
    initialFetchDoneRef.current = false;
    applyCreatedMappingsToDashboard();
    fetchEvent({ background: false }); // Initial fetch should not be background

    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      fetchAttemptRef.current = 0;
      isFetchingRef.current = false;
    };
  }, [isActive]);

  // Handle createdEventMappings changes with debouncing (only if initial fetch is done)
  useEffect(() => {
    if (!isActive || !initialFetchDoneRef.current) return undefined;

    // Apply mappings to UI immediately
    applyCreatedMappingsToDashboard();

    // Debounced API fetch to prevent multiple rapid calls
    debouncedFetchEvent({ background: true, forceRetry: true });

    return undefined;
  }, [createdEventMappings, isActive, initialFetchDoneRef.current]);

  useEffect(() => {
    switch (exam_status) {
      case "missed":
        setSelectedTab("past");
        break;
      case "complete":
        setSelectedTab("past");
        break;
      case "scheduled":
        setSelectedTab("scheduled");
        break;
      default:
        break;
    }
  }, [exam_status]);

  useEffect(() => {
    if (!isLoading && !hasTrackedDashboardView) {
      analytics.trackDashboardViewed();
      setHasTrackedDashboardView(true);
    }
  }, [isLoading, hasTrackedDashboardView, analytics]);

  useEffect(() => {
    if (!isLoading && selectedTab === "scheduled" && !hasTrackedScheduledTab) {
      const hasLiveTest = checkHasLiveTest(thisWeekEvents);
      analytics.trackScheduledTabViewed(hasLiveTest);
      setHasTrackedScheduledTab(true);
    }
  }, [isLoading, selectedTab, thisWeekEvents, hasTrackedScheduledTab, analytics]);

  useEffect(() => {
    if (!isLoading && selectedTab === "scheduled" && thisWeekEvents.length === 0 && !hasTrackedEmptyState) {
      analytics.trackDashboardRenderedEmpty(false);
      setHasTrackedEmptyState(true);
    }
  }, [isLoading, selectedTab, thisWeekEvents, hasTrackedEmptyState, analytics]);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    if (tab === "scheduled") {
      setHasTrackedScheduledTab(false);
    }
  };

  const slideVariants = {
    hidden: { x: 100, opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: -100, opacity: 0 }
  };

  return (
    <div className="student__onboarding__contentmenu board__upcoming__box mob_dashboard">
      <h1 className="test-heading">Test</h1>
      <div className="event-type-cont">
        <h3
          className={`events-type ${selectedTab === "scheduled" ? "selected" : ""}`}
          onClick={() => handleTabClick("scheduled")}
        >
          Scheduled
        </h3>
        <h3
          className={`events-type ${selectedTab === "past" ? "selected" : ""}`}
          onClick={() => handleTabClick("past")}
        >
          Past Tests
        </h3>
      </div>

      <AnimatePresence mode="wait">
        {selectedTab === "scheduled" ? (
          <motion.div
            key="scheduled"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={slideVariants}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <StudentDashBoard
              events={events}
              fetchEvents={fetchEvent}
              thisWeekEvents={thisWeekEvents}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </motion.div>
        ) : (
          <motion.div
            key="past"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={slideVariants}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <StudentPastDashBoard
              events={events}
              fetchEvents={fetchEvent}
              pastUnAttemptedEvents={pastUnAttemptedEvents}
              thisWeekEvents={pastAttemptedEvents}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              pastAttemptedEvents={pastAttemptedEvents}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <div style={{ display: "none" }}>
        <div id="crn_id">{user?.crn_id}</div>
        <div id="uam_id">{user?.uam_id}</div>
        <div id="phone_number">{`${user?.isd_code?.replace("+", "")}${user?.phone}`}</div>
      </div>
    </div>
  );
};
